/**
 * Every route path in one place.
 *
 * Referenced by the router, the sidebar, and every navigate() call, so a path can
 * be changed without hunting through JSX for string literals.
 */
import type { LegalDocumentId } from '../types/domain';

/**
 * Document id -> path, for `paths.legal()`.
 *
 * Above `paths` rather than inside it because it is a lookup table the object's own
 * `legal()` reads, and a `Record` typed by `LegalDocumentId` is what makes a missing
 * document a compiler error. The literals are repeated as named keys on `paths`
 * below so a caller that knows which page it wants can say `paths.terms`.
 */
const LEGAL_PATHS: Record<LegalDocumentId, string> = {
  terms: '/terms',
  privacy: '/privacy',
  'license-agreement': '/license-agreement',
};

export const paths = {
  login: '/login',
  resetPassword: '/reset-password',
  authCallback: '/auth/callback',
  verifyEmail: '/verify-email',
  verifyEmailPending: '/verify-email/pending',
  /**
   * The step between creating an account and using the app.
   *
   * ONE SCREEN, and skippable. A new account is signed in the moment signup returns —
   * the base product is included and every core feature already works — so this asks
   * the only question that has an answer worth having on day one: which of the seven
   * products, if any. Anything else (a favourite genre, a vocal style, a use case) is
   * either already a preference in Settings or would be a screen collecting data
   * nothing reads.
   *
   * Inside the authenticated area rather than beside /login, so it cannot be reached
   * by someone who is not signed in, and so it renders in the app rather than as a
   * fourth public page.
   */
  onboarding: '/onboarding',
  dashboard: '/dashboard',
  explore: '/explore',
  create: '/create',
  library: '/library',
  /**
   * The Studio entry point.
   *
   * A real destination now rather than a redirect to the Library. The workspace
   * itself still opens on a specific song (`song()` below), because a production
   * workspace with no track loaded has nothing to work on — so this route resolves
   * which song that is and hands over to it. See routes/StudioEntry.tsx.
   */
  studio: '/studio',
  /**
   * The Studio's project list, even when `/studio` would otherwise resume work.
   *
   * Query-param mode rather than a second route: it is the same screen, asked for
   * explicitly. See routes/StudioEntry.tsx.
   */
  studioProjects: '/studio?projects=1',
  /** The Studio workspace, open on one song. */
  song: (songId: string) => `/library/${songId}`,
  /**
   * The library's Playlists view, and one playlist's contents.
   *
   * Under /playlists rather than /library/playlists because /library/:songId already
   * matches everything one segment deep — a playlist list at /library/playlists would
   * be indistinguishable from a song whose id happened to be "playlists". Two sibling
   * top-level paths, joined by the tab strip, rather than a route that has to guess.
   */
  playlists: '/playlists',
  playlist: (playlistId: string) => `/playlists/${playlistId}`,
  templates: '/templates',
  products: '/products',
  vault: '/vault',
  dna: '/dna',
  genreMixer: '/genre-mixer',
  /**
   * Image Music — one of Create's four routed methods.
   *
   * Its own route rather than a tab inside /create, so it is linkable and so the Create
   * screen can present the four method cards as four destinations. See routes/ImageMusic.tsx
   * for what part of it is real: the Lyria 3 request is built and asserted, but this
   * build has no backend to send it to.
   */
  imageMusic: '/image-music',
  /**
   * Voice Cloner — an upcoming creation method with a real teaser page.
   *
   * A standalone route, not a modal or an accordion under /create, so the sidebar row and
   * the Create card behave like the other methods and so the page can hold its own notify-me
   * state.
   */
  voiceCloner: '/voice-cloner',
  /**
   * Lyrixa AI PUBLISHER — music distribution.
   *
   * A top-level path, not a child of /studio or /library, because the Publisher is a
   * standalone product area rather than a Studio tool — §4 rules out all four of those
   * placements explicitly. Its own nav row points here.
   */
  publisher: '/publisher',
  /** The Publisher dashboard, even when /publisher would otherwise resume draft work. */
  publisherDashboard: '/publisher?dashboard=1',
  /** The Publisher settings section, through the dashboard escape hatch. */
  publisherSettings: '/publisher?dashboard=1&section=settings',
  /** The eight-step release workflow. A full page, not a drawer over the dashboard. */
  publisherNewRelease: '/publisher/releases/new',
  /** One release's detail page. */
  publisherRelease: (releaseId: string) => `/publisher/releases/${releaseId}`,
  licenses: '/licenses',
  reseller: '/reseller',
  /** PUBLIC — no authentication required. Blank manual lookup page. */
  verifyLicense: '/verify-license',
  /** PUBLIC — no authentication required. QR/deep-link verification route. */
  licenseVerify: (licenseId: string) => `/license/verify/${licenseId}`,
  /**
   * PUBLIC — a shared song, addressed by its share slug. No authentication.
   *
   * The absolute URL a user copies is built by lib/share.ts (it has to name the
   * production host, since the point is to paste it elsewhere); this is the
   * in-app path the router matches.
   */
  share: (slug: string) => `/share/${slug}`,
  agreement: '/agreement',
  /**
   * PUBLIC — the legal documents. No authentication.
   *
   * Siblings of the shell alongside /login, /share and /license/verify, for the reason
   * §4 gives: these have to be readable without an account, and the terms link in the
   * signup form is clicked by somebody who does not have one yet. A legal page behind
   * the auth gate would be a page nobody could read before agreeing to it.
   *
   * SEPARATE TOP-LEVEL PATHS RATHER THAN /legal/:id, deliberately. `/terms` and
   * `/privacy` are the URLs people type, link to and expect — they are the two most
   * conventional paths on the web — and burying them a segment deep to save a route
   * entry would trade a familiar URL for nothing. `legal()` maps an id to its path so
   * the cross-links and the footer do not each hold a table.
   */
  terms: '/terms',
  privacy: '/privacy',
  licenseAgreement: '/license-agreement',
  /**
   * One legal document's path, by id.
   *
   * A function over the literals above rather than a second string table, so the
   * router, the footer, the signup checkbox and the cross-link row all resolve an id
   * the same way — and so `LegalDocumentId` gaining a fifth member is a compiler error
   * here rather than a link nobody wrote.
   */
  legal: (document: LegalDocumentId): string => LEGAL_PATHS[document],
  settings: '/settings',
  settingsProfile: '/settings/profile',
  settingsBilling: '/settings/billing',
  settingsSecurity: '/settings/security',
  admin: '/admin',
  adminSupport: '/admin/support',
  adminUsers: '/admin/users',
  adminArchive: '/admin/archive',
  adminMail: '/admin/mail',
  adminBonuses: '/admin/bonuses',
  adminVault: '/admin/vault',
  topup: '/topup',
  upgrade: '/upgrade',
  guide: '/guide',
  visualGuide: '/fullguide',
  support: '/support',
  supportTopic: (topicId: string) => `/support/${topicId}`,
  ticket: '/ticket',
  bonus: '/bonus',
} as const;

/*
  Removed in the Phase 2 cleanup: a `labelForPath()` helper and its 21-entry
  prefix->label table, ported from the monolith's VIEW_LABELS.

  It had no caller and no route to becoming one. BackLink is the only consumer of
  a "Back to X" label and it takes that label as an explicit `fallbackLabel`
  prop, which is deliberate — the label names the destination the button will
  actually navigate to, so deriving it from the *current* pathname would have
  produced "Back to Support ticket" while standing on the ticket page. In history
  mode BackLink cannot know the previous entry at all and renders a bare "Back".

  Noted here rather than deleted silently because the table looks like preserved
  monolith behaviour. It is not: no label rendered anywhere came from it.
*/
